import {
  Form as RemixForm,
  useActionData,
  ActionFunction,
  Link,
} from "react-router";
import { requestPasswordReset } from "~/utils/auth.server";
import {
  Card,
  Text,
  Button,
  Flex,
  Heading,
  TextField,
  Callout,
} from "@radix-ui/themes";
import { CheckCircledIcon } from "@radix-ui/react-icons";
import * as Form from "@radix-ui/react-form";

type ActionData = {
  ok?: true;
  errors?: {
    general?: string;
    email?: string;
  };
};

export const action: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  const email = formData.get("email") as string;

  if (!email) {
    return { errors: { email: "Email is required" } };
  }

  try {
    return await requestPasswordReset(email);
  } catch (error: unknown) {
    if (error && typeof error === "object" && "details" in error) {
      return { errors: error.details as Record<string, string> };
    }
    const message = error instanceof Error ? error.message : "Request failed";
    return { errors: { general: message } };
  }
};

export default function ForgotPassword() {
  const actionData = useActionData<ActionData>();

  return (
    <Flex align="center" justify="center" className="h-screen">
      <Card variant="surface" className="max-w-sm w-full shadow-lg p-6">
        <Heading size="6" weight="bold" mb="4">
          Forgot Password
        </Heading>

        {actionData?.ok && (
          <Callout.Root color="green" mb="4">
            <Callout.Icon>
              <CheckCircledIcon />
            </Callout.Icon>
            <Callout.Text>
              If that email is registered, we&apos;ve sent a reset link. Check
              your inbox.
            </Callout.Text>
          </Callout.Root>
        )}

        {actionData?.errors?.general && (
          <Text color="ruby" size="2" mb="4">
            {actionData.errors.general}
          </Text>
        )}

        <Form.Root asChild>
          <RemixForm method="post" action="/forgot-password">
            <Form.Field name="email" className="mb-4">
              <Form.Label asChild>
                <Text as="label" size="1">
                  Email
                </Text>
              </Form.Label>
              <Form.Control asChild>
                <TextField.Root
                  placeholder="Enter your email"
                  type="email"
                  required
                />
              </Form.Control>
              {actionData?.errors?.email && (
                <Text size="1" color="ruby">
                  {actionData.errors.email}
                </Text>
              )}
              <Form.Message asChild match="valueMissing">
                <Text size="1" color="ruby">
                  Email is required
                </Text>
              </Form.Message>
              <Form.Message asChild match="typeMismatch">
                <Text size="1" color="ruby">
                  Please enter a valid email address
                </Text>
              </Form.Message>
            </Form.Field>

            <Form.Submit asChild>
              <Button className="w-full mb-4">Send reset link</Button>
            </Form.Submit>
          </RemixForm>
        </Form.Root>

        <Text size="1" align="center">
          Remembered it?{" "}
          <Link to="/login" className="text-blue-600 hover:underline">
            Back to login
          </Link>
        </Text>
      </Card>
    </Flex>
  );
}
